import type { List as ImmutableList } from 'immutable';

import { chartOfAccountsTypes, chartOfAccountsUtils } from 'app-components/flux/chart-of-accounts';

import type { SplitItemProps, TransactionProps } from './transactionsTypes';

type SplitItemsLike = Array<unknown> | ImmutableList<unknown>;

// Made to work with JS objects and Immutable lists.
export function isSplitTxn(txn: { split?: { items?: SplitItemsLike | null } | null } | null | undefined): boolean {
  return Boolean(
    txn &&
      txn.split &&
      txn.split.items &&
      ('size' in txn.split.items ? txn.split.items.size > 0 : txn.split.items.length > 0),
  );
}

// Only actual transfers. The base "transfer" category is technically a balance adjustment,
// so it will not be true for this helper. Use `isUITransferTxn` if you need the L1 Transfer category.
export function isTransferTxn(txn: Partial<TransactionProps> | Partial<SplitItemProps> | null | undefined): boolean {
  if (txn && 'split' in txn && isSplitTxn(txn)) {
    return Boolean(txn.split?.items?.find((item) => Boolean(item.coa && item.coa.type === 'ACCOUNT')));
  }

  return Boolean(
    txn && txn.coa && txn.coa.type === 'ACCOUNT' && (!('accountId' in txn) || txn.coa.id !== txn.accountId),
  );
}

// Includes the L1 Transfer option as well as actual account COAs.
export function isUITransferTxn(txn: TransactionProps | null | undefined): boolean {
  if (isSplitTxn(txn)) {
    return Boolean(
      txn?.split?.items?.find((item) =>
        Boolean(
          item.coa &&
            (item.coa.type === 'ACCOUNT' ||
              (item.coa.type === 'BALANCE_ADJUSTMENT' &&
                item.coa.id !== chartOfAccountsTypes.BalanceAdjustmentTypeEnum.CREDIT_CARD_PAYMENT)),
        ),
      ),
    );
  }

  return Boolean(
    txn &&
      txn.coa &&
      ((txn.coa.type === 'ACCOUNT' && txn.coa.id !== txn.accountId) ||
        (txn.coa.type === 'BALANCE_ADJUSTMENT' &&
          txn.coa.id !== chartOfAccountsTypes.BalanceAdjustmentTypeEnum.CREDIT_CARD_PAYMENT)),
  );
}

export function isTransferOrCreditCardPaymentTxn(txn: TransactionProps | SplitItemProps | null | undefined): boolean {
  if (txn && 'split' in txn && isSplitTxn(txn)) {
    return Boolean(
      txn.split?.items?.find((item) =>
        Boolean(
          item.coa &&
            (item.coa.type === 'ACCOUNT' || chartOfAccountsUtils.isTransferLikeBalanceAdjustmentCoa(item.coa)),
        ),
      ),
    );
  }

  return Boolean(
    txn &&
      txn.coa &&
      ((txn.coa.type === 'ACCOUNT' &&
        (!('accountId' in txn) || ('accountId' in txn && txn.coa.id !== txn.accountId))) ||
        chartOfAccountsUtils.isTransferLikeBalanceAdjustmentCoa(txn.coa)),
  );
}

export function hasExplicitSplitExclusionOverride(
  split: Pick<SplitItemProps, 'isExcludedFromF2S' | 'isExcludedFromReports'>,
): boolean {
  return split.isExcludedFromF2S != null || split.isExcludedFromReports != null;
}
